import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Button } from "@/components/ui/button";
import { X, ShoppingBag, Clock, Eye } from "lucide-react";
import Receipt from "@/components/Receipt";

export default function CustomerHistory({ customer, store, onClose }) {
  const [data, setData] = useState({ sales: [], open_orders: [] });
  const [showReceipt, setShowReceipt] = useState(null);

  useEffect(() => {
    api.get(`/customers/${customer.id}/history`, { _cache: false }).then(r => setData(r.data));
  }, [customer.id]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";

  const renderRow = (o, isOpen) => (
    <div key={o.id} className="p-3 border border-[#E5E5E5] rounded-md flex items-start justify-between gap-3 hover:border-neutral-300 transition" data-testid={`history-row-${o.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">#{String(o.order_number || "").padStart(6, "0")}</span>
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
            isOpen ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
          }`}>{o.status}</span>
        </div>
        <div className="text-xs text-neutral-700 mt-1 line-clamp-2">
          {(o.items || []).map(it => `${it.quantity}× ${it.name}`).join(", ") || "—"}
        </div>
        <div className="text-[10px] text-neutral-400 mt-1">{fmt(o.created_at)} · {o.payment_method === "Pendente programado" ? "Crediário" : o.payment_method}</div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="font-display text-base">{brl(o.total)}</div>
        <Button size="sm" variant="outline" onClick={()=>setShowReceipt(o)} className="h-7 text-[11px]" data-testid={`view-receipt-${o.id}`}>
          <Eye size={11} className="mr-1"/> Recibo
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 no-print" data-testid="customer-history-modal">
      <div className="bg-white max-w-lg w-full max-h-[92vh] overflow-y-auto rounded-md">
        <div className="p-5 border-b border-[#E5E5E5] flex justify-between items-center">
          <div>
            <p className="label-tiny">Cliente · Histórico</p>
            <h2 className="font-display text-xl mt-0.5">{customer.name}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock size={13} className="text-amber-600"/>
              <p className="label-tiny">Pedidos em aberto</p>
              <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{data.open_orders.length}</span>
            </div>
            {data.open_orders.length === 0 ? (
              <p className="text-xs text-neutral-500 py-3 text-center">Nenhum pedido em aberto.</p>
            ) : (
              <div className="space-y-2">{data.open_orders.map(o => renderRow(o, true))}</div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag size={13} className="text-emerald-600"/>
              <p className="label-tiny">Vendas finalizadas</p>
              <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">{data.sales.length}</span>
            </div>
            {data.sales.length === 0 ? (
              <p className="text-xs text-neutral-500 py-3 text-center">Nenhuma venda finalizada.</p>
            ) : (
              <div className="space-y-2">{data.sales.map(o => renderRow(o, false))}</div>
            )}
          </div>
        </div>
      </div>

      {showReceipt && <Receipt sale={showReceipt} store={store} wideLayout onClose={()=>setShowReceipt(null)}/>}
    </div>
  );
}
